import { createContext, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { countSelfSchedulePeriodsOptions } from '@drivingdoc/api-client/generated/react-query';
import useAuthContext from 'hooks/useAuthContext';
import useStudentContext from 'hooks/useStudentContext';
import { SelfScheduledPeriodCount, StudentCourseEnrollment } from 'types/students';

interface SelfScheduleParams {
    selectedMonth: Dayjs;
    selectedDate: Dayjs;
    selectedEnrollment?: StudentCourseEnrollment;
}

interface ISelfScheduleContext {
    periodCountLoading: boolean;
    periodCount: SelfScheduledPeriodCount;
    selfScheduleParams: SelfScheduleParams;
    setSelfScheduleParams: React.Dispatch<React.SetStateAction<SelfScheduleParams>>;
}

// create self schedule context with default values
const SelfScheduleContext = createContext<ISelfScheduleContext>({
  periodCountLoading: true,
  periodCount: {},
  selfScheduleParams: {
    selectedMonth: dayjs().startOf('month'),
    selectedDate: dayjs().startOf('day'),
    selectedEnrollment: undefined,
  },
  setSelfScheduleParams: () => {},
});

interface PropTypes {
  children: JSX.Element;
}

function SelfScheduleContextProvider({ children }: PropTypes) {
  // get the student id from the auth context
  const { userId: studentId } = useAuthContext();

  const { courseEnrollments } = useStudentContext();

  const [selfScheduleParams, setSelfScheduleParams] = useState<SelfScheduleParams>({
    selectedMonth: dayjs().startOf('month'),
    selectedDate: dayjs().startOf('day'),
    selectedEnrollment: undefined,
  });

  // fetch self scheduled period count for the current month
  const { data: periodCountData, isLoading: periodCountLoading } = useQuery({
    ...countSelfSchedulePeriodsOptions({
      path: {
        id: studentId ?? '',
        courseEnrollmentId: selfScheduleParams?.selectedEnrollment?.id ?? '',
      },
      query: {
        start: selfScheduleParams?.selectedMonth.startOf('month').toISOString(),
        end: selfScheduleParams?.selectedMonth.endOf('month').toISOString(),
      },
    }),
    enabled: !!studentId && !!selfScheduleParams?.selectedEnrollment?.id && !!selfScheduleParams?.selectedMonth,
  });
  const periodCount = periodCountData as SelfScheduledPeriodCount | undefined;

  // set the default course enrollment to the first self scheduled course
  useEffect(() => {
    if (courseEnrollments?.length > 0 && !selfScheduleParams?.selectedEnrollment) {
      setSelfScheduleParams((prev) => {
        const selfScheduledCourseEnrollments = courseEnrollments.filter(
          (enrollment) => enrollment?.course?.schedule_type === 'self_scheduled',
        );
        return {
          ...prev,
          selectedEnrollment: selfScheduledCourseEnrollments?.[0],
        };
      });
    }
  }, [courseEnrollments, selfScheduleParams?.selectedEnrollment]);

  return (
    <SelfScheduleContext.Provider
      value={{
        periodCountLoading: periodCountLoading,
        periodCount: periodCount ?? {},
        selfScheduleParams: selfScheduleParams,
        setSelfScheduleParams: setSelfScheduleParams,
      }}
    >
      {children}
    </SelfScheduleContext.Provider>
  );
}

export type { ISelfScheduleContext };
export { SelfScheduleContext, SelfScheduleContextProvider };